import { Suspense, useEffect } from "react";
import { AppErrorFallback, ErrorBoundary } from "@/components/tpr/ErrorBoundary";
import { Sentry } from "@/core/sentry";
import { setGlobalQueryClient } from "@/lib/queryClient";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReactGA from "react-ga4";
import { BrowserRouter, Route, Routes } from "react-router";
import Footer from "./components/tpr/Footer/Footer";
import LinkInterceptor from "./components/tpr/LinkInterceptor/LinkInterceptor";
import ScrollToTop from "./components/tpr/ScrollToTop/ScrollToTop";
import AcceptableUsePolicyView from "./views/AcceptableUsePolicyView";
import AccountView from "./views/AccountView";
import AllMyBookingsView from "./views/AllMyBookingsView";
import BillingView from "./views/BillingView";
import OrganisationView from "./views/OrganisationView";
import CookiesPolicyView from "./views/CookiesPolicyView";
import Dashboard from "./views/Dashboard";
import ForgottenPasswordView from "./views/ForgottenPasswordView";
import HelpArticleView from "./views/HelpArticleView";
import HelpCategoryView from "./views/HelpCategoryView";
import HelpView from "./views/HelpView";
import LocationRoomSchedule from "./views/LocationRoomSchedule";
import LocationView from "./views/LocationView";
import Login from "./views/Login";
import LoginTerms from "./views/LoginTerms";
import LoginDeclaration from "./views/LoginDeclaration";
import MessagesView from "./views/MessagesView";
import Mfa from "./views/Mfa";
import MfaAlternative from "./views/MfaAlternative";
import NotFound from "./views/NotFound";
import NotificationsView from "./views/NotificationsView";
import PrivacyPolicyView from "./views/PrivacyPolicyView";
import ResetPasswordView from "./views/ResetPasswordView";
import CreatePassword from "./views/CreatePassword";
import RoomView from "./views/RoomView";
import SettingsView from "./views/SettingsView";
import TermsView from "./views/TermsView";
import ViewBooking from "./views/ViewBooking";
import TPR from "./wrappers/TPR";
import RequireMessagesEnabled from "./components/tpr/RequireMessagesEnabled/RequireMessagesEnabled";

// Admin and Practice Manager views are role-restricted and only loaded on demand.
const AdminBookingsView = lazyWithRetry(() => import("./views/admin/AdminBookingsView"));
const AdminFinancesView = lazyWithRetry(() => import("./views/admin/AdminFinancesView"));
const AdminLocationEditView = lazyWithRetry(() => import("./views/admin/AdminLocationEditView"));
const AdminLocationsView = lazyWithRetry(() => import("./views/admin/AdminLocationsView"));
const AdminMembersView = lazyWithRetry(() => import("./views/admin/AdminMembersView"));
const AdminMemberView = lazyWithRetry(() => import("./views/admin/AdminMemberView"));
const AdminOrganisationsView = lazyWithRetry(() => import("./views/admin/AdminOrganisationsView"));
const AdminOrganisationDetailView = lazyWithRetry(() => import("./views/admin/AdminOrganisationDetailView"));
const AdminReportsView = lazyWithRetry(() => import("./views/admin/AdminReportsView"));
const AdminRoomEditView = lazyWithRetry(() => import("./views/admin/AdminRoomEditView"));
const AdminSettingsView = lazyWithRetry(() => import("./views/admin/AdminSettingsView"));
const AdminDevToolsLogsView = lazyWithRetry(() => import("./views/admin/devTools/AdminDevToolsLogsView"));
const AdminDevToolsOnlineMembersView = lazyWithRetry(() => import("./views/admin/devTools/AdminDevToolsOnlineMembersView"));
const AdminDevToolsToolsView = lazyWithRetry(() => import("./views/admin/devTools/AdminDevToolsToolsView"));
const AdminMessagesView = lazyWithRetry(() => import("./views/admin/AdminMessagesView"));
const AdminNoticesView = lazyWithRetry(() => import("./views/admin/AdminNoticesView"));
const AdminHelpView = lazyWithRetry(() => import("./views/admin/HelpAdminView"));
const AllLocationsView = lazyWithRetry(() => import("./views/practiceManager/AllLocationsView"));
// Dev-only diagnostic page: proves booking-rule enforcement against the real validators. It imports
// Ember source cross-repo (only present in the local monorepo), so it is EXCLUDED from production
// builds — on Netlify the ember repo isn't checked out. import.meta.env.DEV is statically false in
// `vite build`, so this dynamic import (and its ember deps) is tree-shaken out of the bundle.
const BookingRulesHarness = import.meta.env.DEV
  ? lazyWithRetry(() => import("./dev/bookingRulesHarness/BookingRulesHarness"))
  : null;
const CancellationRulesHarness = import.meta.env.DEV
  ? lazyWithRetry(() => import("./dev/cancellationRulesHarness/CancellationRulesHarness"))
  : null;
const PracticeAmenitiesView = lazyWithRetry(() => import("./views/practiceManager/PracticeAmenitiesView"));
const PracticeAmenityEditView = lazyWithRetry(() => import("./views/practiceManager/PracticeAmenityEditView"));
const PracticeBookingsView = lazyWithRetry(() => import("./views/practiceManager/PracticeBookingsView"));
const PracticeBookingView = lazyWithRetry(() => import("./views/practiceManager/PracticeBookingView"));
const PracticeMembersView = lazyWithRetry(() => import("./views/practiceManager/PracticeMembersView"));
const PracticeMemberView = lazyWithRetry(() => import("./views/practiceManager/PracticeMemberView"));
const PracticeNoticesView = lazyWithRetry(() => import("./views/practiceManager/PracticeNoticesView"));
const PracticeReportsView = lazyWithRetry(() => import("./views/practiceManager/PracticeReportsView"));
const PracticeRoomEditView = lazyWithRetry(() => import("./views/practiceManager/PracticeRoomEditView"));
const PracticeRoomsView = lazyWithRetry(() => import("./views/practiceManager/PracticeRoomsView"));
const PracticeOrganisationsView = lazyWithRetry(() => import("./views/practiceManager/PracticeOrganisationsView"));
const PracticeOrganisationDetailView = lazyWithRetry(() => import("./views/practiceManager/PracticeOrganisationDetailView"));
const PracticeSettingsView = lazyWithRetry(() => import("./views/practiceManager/PracticeSettingsView"));

function App() {
  const staleTime = import.meta.env.VITE_APP_ENV === "development" ? 10 * 1000 : 2 * 60 * 1000; // 10 seconds for dev, 2 minutes for prod

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: staleTime,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          if (error && typeof error === "object" && "status" in error) {
            const status = (error as any).status;
            if (status >= 400 && status < 500) return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        onError: (error) => {
          // Report mutation errors to Sentry
          Sentry.captureException(error, {
            tags: { error_type: "react_query_mutation_error" },
          });
        },
      },
    },
  });

  // Register queryClient globally for use in utility functions
  // This enables cache invalidation from non-React code
  useEffect(() => {
    setGlobalQueryClient(queryClient);
  }, [queryClient]);

  useEffect(() => {
    if (import.meta.env.VITE_GA4_MEASUREMENT_ID) {
      ReactGA.initialize(import.meta.env.VITE_GA4_MEASUREMENT_ID);
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }
  }, []);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <div id="app" style={{ minWidth: "375px" }}>
          <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary level="app" fallback={AppErrorFallback}>
              <Suspense fallback={null}>
                <Routes>
                <Route path="*" element={<NotFound />} />
                <Route path="/" element={<Login />} />
                <Route path="/login/terms" element={<LoginTerms />} />
                <Route path="/login/declaration" element={<LoginDeclaration />} />
                <Route
                  path="/link/*"
                  element={
                    <TPR>
                      <LinkInterceptor />
                    </TPR>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgottenPasswordView />} />
                <Route path="/reset-password" element={<ResetPasswordView />} />
                <Route path="/create-password" element={<CreatePassword />} />
                <Route path="/mfa" element={<Mfa />} />
                <Route path="/mfa-alternative" element={<MfaAlternative />} />
                {/* Dev-only booking-rules diagnostic harness — excluded from production builds (imports
                    Ember source that isn't present on Netlify). No auth wrapper; builds its own fixtures. */}
                {import.meta.env.DEV && BookingRulesHarness && (
                  <Route path="/dev/booking-rules-harness" element={<BookingRulesHarness />} />
                )}
                {/* Dev-only cancellation-rules diagnostic harness — same prod-exclusion rationale as above. */}
                {import.meta.env.DEV && CancellationRulesHarness && (
                  <Route path="/dev/cancellation-rules-harness" element={<CancellationRulesHarness />} />
                )}
                <Route
                  path="/dashboard"
                  element={
                    <TPR>
                      <Dashboard />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug"
                  element={
                    <TPR>
                      <LocationView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/calendar"
                  element={
                    <TPR>
                      <LocationRoomSchedule />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/room/:roomId"
                  element={
                    <TPR>
                      <RoomView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/members"
                  element={
                    <TPR>
                      <AdminMembersView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/members/:memberId"
                  element={
                    <TPR>
                      <AdminMemberView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/bookings"
                  element={
                    <TPR>
                      <AdminBookingsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/help"
                  element={
                    <TPR>
                      <AdminHelpView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/notices"
                  element={
                    <TPR>
                      <AdminNoticesView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/locations"
                  element={
                    <TPR>
                      <AdminLocationsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/locations/:locationSlug"
                  element={
                    <TPR>
                      <AdminLocationEditView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/locations/:locationSlug/rooms/:roomId"
                  element={
                    <TPR>
                      <AdminRoomEditView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/finances"
                  element={
                    <TPR>
                      <AdminFinancesView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/reports"
                  element={
                    <TPR>
                      <AdminReportsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/organisations"
                  element={
                    <TPR>
                      <AdminOrganisationsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/organisations/:organisationId"
                  element={
                    <TPR>
                      <AdminOrganisationDetailView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <TPR>
                      <AdminSettingsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/messages"
                  element={
                    <TPR>
                      <RequireMessagesEnabled>
                        <AdminMessagesView />
                      </RequireMessagesEnabled>
                    </TPR>
                  }
                />
                <Route
                  path="/admin/dev-tools/online-members"
                  element={
                    <TPR>
                      <AdminDevToolsOnlineMembersView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/dev-tools/logs"
                  element={
                    <TPR>
                      <AdminDevToolsLogsView />
                    </TPR>
                  }
                />
                <Route
                  path="/admin/dev-tools/tools"
                  element={
                    <TPR>
                      <AdminDevToolsToolsView />
                    </TPR>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <TPR>
                      <RequireMessagesEnabled>
                        <MessagesView />
                      </RequireMessagesEnabled>
                    </TPR>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <TPR>
                      <HelpView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/help/category/:slug"
                  element={
                    <TPR>
                      <HelpCategoryView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/help/article/:slug"
                  element={
                    <TPR>
                      <HelpArticleView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <TPR>
                      <SettingsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <TPR>
                      <AccountView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <TPR>
                      <BillingView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/organisation"
                  element={
                    <TPR>
                      <OrganisationView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <TPR>
                      <NotificationsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/view-booking/:bookingId"
                  element={
                    <TPR>
                      <ViewBooking />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/all-bookings"
                  element={
                    <TPR>
                      <AllMyBookingsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/all-locations"
                  element={
                    <TPR>
                      <AllLocationsView />
                    </TPR>
                  }
                />
                <Route
                  path="/practice/organisations"
                  element={
                    <TPR>
                      <PracticeOrganisationsView />
                    </TPR>
                  }
                />
                <Route
                  path="/practice/organisations/:organisationId"
                  element={
                    <TPR>
                      <PracticeOrganisationDetailView />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-members"
                  element={
                    <TPR>
                      <PracticeMembersView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-members/:memberId"
                  element={
                    <TPR>
                      <PracticeMemberView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-bookings"
                  element={
                    <TPR>
                      <PracticeBookingsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-bookings/:bookingId"
                  element={
                    <TPR>
                      <PracticeBookingView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-amenities/manage"
                  element={
                    <TPR>
                      <PracticeAmenityEditView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-amenities"
                  element={
                    <TPR>
                      <PracticeAmenitiesView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-rooms"
                  element={
                    <TPR>
                      <PracticeRoomsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-rooms/:roomId"
                  element={
                    <TPR>
                      <PracticeRoomEditView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-notices"
                  element={
                    <TPR>
                      <PracticeNoticesView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-reports"
                  element={
                    <TPR>
                      <PracticeReportsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/location/:locationSlug/practice-settings"
                  element={
                    <TPR>
                      <PracticeSettingsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <TPR>
                      <TermsView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/terms/acceptable-use-policy"
                  element={
                    <TPR>
                      <AcceptableUsePolicyView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/terms/cookies-policy"
                  element={
                    <TPR>
                      <CookiesPolicyView />
                      <Footer />
                    </TPR>
                  }
                />
                <Route
                  path="/terms/privacy-policy"
                  element={
                    <TPR>
                      <PrivacyPolicyView />
                      <Footer />
                    </TPR>
                  }
                />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </div>
      </QueryClientProvider>
    </>
  );
}

export default App;
